import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { SiteChatVoice } from '../types';
import type { SiteChatUi } from '../ui';
import { EMOJI_GROUPS, EMOJI_GROUP_ICONS } from './emoji';

/**
 * Emoji and voice messages of the composer, ported from the in-app chat's `CommentFormEditor`
 * (`accel-framework/components/CommentForm`: `EmojiInput`, `VoiceInput`, `useAudioRecorder`, `useAudioContext`)
 * without antd — the widget is a standalone bundle in a Shadow DOM. Same gestures: hold the microphone to record,
 * drag up to lock, release outside the field (or slide left on a touch screen) to cancel; a finished recording
 * waits in the composer with a player until it is sent or deleted.
 */

const ICON = {
    smile: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8.5 14.5s1.3 2 3.5 2 3.5-2 3.5-2M9 9.5h.01M15 9.5h.01',
    mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM19 11a7 7 0 0 1-14 0M12 18v3',
    lock: 'M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z',
    up: 'M12 19V9M7 13l5-5 5 5',
    stop: 'M7 7h10v10H7z',
    play: 'M8 5.5v13l10.5-6.5z',
    pause: 'M8 5h3v14H8zM13 5h3v14h-3z',
    trash: 'M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3',
    left: 'M15 6l-6 6 6 6',
};

export function MediaIcon({ d, size = 20, fill = false }: { d: string; size?: number; fill?: boolean }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={d} />
        </svg>
    );
}

// ---------- emoji ----------

export function EmojiButton({ ui, onSelect, disabled }: { ui: SiteChatUi; onSelect: (emoji: string) => void; disabled?: boolean }) {
    const [open, setOpen] = useState(false);
    const [group, setGroup] = useState(0);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open)
            return;
        // The widget lives in a Shadow DOM: `composedPath` sees through it, `event.target` would be the host.
        const onDown = (e: PointerEvent) => {
            if (wrapRef.current && !e.composedPath().includes(wrapRef.current))
                setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="axl-sc-emw" ref={wrapRef}>
            <button type="button" className="axl-sc-cb" aria-label={ui.emoji} aria-expanded={open} disabled={disabled}
                data-sc="emoji" onClick={() => setOpen(!open)}>
                <MediaIcon d={ICON.smile} />
            </button>
            {open ? (
                <div className="axl-sc-emp" role="dialog" aria-label={ui.emoji}>
                    <div className="axl-sc-emt" role="tablist">
                        {EMOJI_GROUP_ICONS.map((icon, index) => (
                            <button key={index} type="button" role="tab" aria-selected={group === index} title={ui.emojiGroups[index]}
                                className={group === index ? 'on' : ''} onClick={() => setGroup(index)}>{icon}</button>
                        ))}
                    </div>
                    <div className="axl-sc-emh">{ui.emojiGroups[group]}</div>
                    <div className="axl-sc-emg">
                        {EMOJI_GROUPS[group].map((emoji, index) => (
                            <button key={`${group}-${index}`} type="button" onClick={() => onSelect(emoji)}>{emoji}</button>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// ---------- voice player ----------

const BARS = 36;

/** Resamples the recorder's 128 levels to the bars the bubble has room for; a flat line without levels. */
function bars(levels: number[] | null | undefined): number[] {
    if (!levels?.length)
        return Array.from({ length: BARS }, () => 0.15);
    return Array.from({ length: BARS }, (_, i) => {
        const from = Math.floor(i * levels.length / BARS);
        const to = Math.max(from + 1, Math.floor((i + 1) * levels.length / BARS));
        const slice = levels.slice(from, to);
        return Math.max(0.12, slice.reduce((sum, value) => sum + value, 0) / slice.length);
    });
}

export function formatDuration(seconds: number) {
    const total = Math.max(0, Math.round(seconds || 0));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function VoicePlayer({ url, duration, levels, ui }: { url: string; duration?: number | null; levels?: number[] | null; ui: SiteChatUi }) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);
    const [time, setTime] = useState(0);
    const [length, setLength] = useState(duration ?? 0);
    const heights = useMemo(() => bars(levels), [levels]);
    const progress = length > 0 ? Math.min(1, time / length) : 0;

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio)
            return;
        if (audio.paused)
            void audio.play().catch(() => setPlaying(false));
        else
            audio.pause();
    };

    const seek = (e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !length)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        audio.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * length;
        setTime(audio.currentTime);
    };

    return (
        <div className="axl-sc-vp" data-sc="voice">
            <button type="button" className="axl-sc-vpb" aria-label={playing ? ui.pause : ui.play} onClick={toggle}>
                <MediaIcon d={playing ? ICON.pause : ICON.play} size={16} fill />
            </button>
            <div className="axl-sc-vpw" onClick={seek} role="presentation" aria-label={ui.voiceMessage}>
                {heights.map((height, i) => (
                    <i key={i} className={i / BARS < progress ? 'on' : ''} style={{ height: `${Math.round(height * 100)}%` }} />
                ))}
            </div>
            <span className="axl-sc-vpt">{formatDuration(playing || time > 0 ? time : length)}</span>
            <audio
                ref={audioRef}
                src={url || undefined}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => {
                    setPlaying(false);
                    setTime(0);
                }}
                onTimeUpdate={e => setTime(e.currentTarget.currentTime)}
                onLoadedMetadata={e => {
                    const value = e.currentTarget.duration;
                    if (Number.isFinite(value) && value > 0)
                        setLength(value);
                }}
            />
        </div>
    );
}

// ---------- recording ----------

/** Waveform and duration of a recording, the in-app chat's `useAudioContext().getMeta`. */
async function measure(blob: Blob, fallbackSeconds: number): Promise<{ duration: number; levels: number[] }> {
    const flat = { duration: fallbackSeconds, levels: [] as number[] };
    const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context || blob.size >= 10 * 1024 * 1024)
        return flat;
    const context = new Context();
    try {
        const buffer = await context.decodeAudioData(await blob.arrayBuffer());
        const data = buffer.getChannelData(0);
        const samples = 128;
        const block = Math.max(1, Math.floor(data.length / samples));
        const levels: number[] = [];
        for (let i = 0; i < samples; i++) {
            let sum = 0;
            for (let j = 0; j < block; j++)
                sum += Math.abs(data[i * block + j] ?? 0);
            levels.push(sum / block);
        }
        const max = Math.max(...levels) || 1;
        return { duration: buffer.duration || fallbackSeconds, levels: levels.map(value => Number((value / max).toPrecision(3))) };
    } catch {
        return flat;
    } finally {
        void context.close().catch(() => undefined);
    }
}

type RecorderState = 'idle' | 'recording' | 'locked';

export type VoiceRecorderProps = {
    ui: SiteChatUi;
    disabled?: boolean;
    /** The row the overlay covers while recording; leaving it cancels on release. */
    areaRef: React.RefObject<HTMLDivElement | null>;
    onRecorded: (voice: SiteChatVoice) => void;
    onRecordingChange: (recording: boolean) => void;
    onError: (message: string) => void;
};

/** The microphone button and, while it records, the overlay over the composer row. */
export function VoiceRecorder({ ui, disabled, areaRef, onRecorded, onRecordingChange, onError }: VoiceRecorderProps) {
    const [state, setState] = useState<RecorderState>('idle');
    const [seconds, setSeconds] = useState(0);
    const [outside, setOutside] = useState(false);
    const [dragUp, setDragUp] = useState(0);
    const [dragLeft, setDragLeft] = useState(0);
    const isTouch = useMemo(() => typeof window !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window), []);

    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<number | null>(null);
    const holdTimerRef = useRef<number | null>(null);
    const pressedRef = useRef(false);
    const recordingRef = useRef(false);
    const lockedRef = useRef(false);
    const outsideRef = useRef(false);
    const cancelledRef = useRef(false);
    const startedAtRef = useRef(0);
    const originRef = useRef({ x: 0, y: 0 });

    const reset = useCallback(() => {
        recordingRef.current = false;
        lockedRef.current = false;
        outsideRef.current = false;
        cancelledRef.current = false;
        pressedRef.current = false;
        setState('idle');
        setOutside(false);
        setDragUp(0);
        setDragLeft(0);
        setSeconds(0);
        if (timerRef.current)
            window.clearInterval(timerRef.current);
        timerRef.current = null;
        onRecordingChange(false);
    }, [onRecordingChange]);

    const stop = useCallback((cancel: boolean) => {
        cancelledRef.current = cancel;
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive')
            recorder.stop();
        else
            reset();
    }, [reset]);

    const start = useCallback(async () => {
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: { noiseSuppression: true, echoCancellation: true } });
        } catch {
            pressedRef.current = false;
            onError(ui.micDenied);
            return;
        }
        // Released before the microphone answered: nothing to record.
        if (!pressedRef.current && !lockedRef.current) {
            stream.getTracks().forEach(track => track.stop());
            onError(ui.recordHold);
            return;
        }
        const recorder = new MediaRecorder(stream);
        chunksRef.current = [];
        recorder.addEventListener('dataavailable', e => {
            if (e.data?.size)
                chunksRef.current.push(e.data);
        });
        recorder.addEventListener('stop', () => {
            stream.getTracks().forEach(track => track.stop());
            recorderRef.current = null;
            const keep = !cancelledRef.current && (lockedRef.current || !outsideRef.current);
            const elapsed = (Date.now() - startedAtRef.current) / 1000;
            const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
            reset();
            if (!keep || blob.size === 0 || elapsed < 0.5)
                return;
            void measure(blob, elapsed).then(meta => onRecorded({
                blob,
                duration: meta.duration,
                levels: meta.levels,
                mimeType: blob.type,
            }));
        });
        recorderRef.current = recorder;
        recorder.start();
        startedAtRef.current = Date.now();
        recordingRef.current = true;
        cancelledRef.current = false;
        setState(lockedRef.current ? 'locked' : 'recording');
        onRecordingChange(true);
        timerRef.current = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000)), 250);
    }, [onError, onRecorded, onRecordingChange, reset, ui]);

    const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (disabled || state !== 'idle')
            return;
        e.preventDefault();
        pressedRef.current = true;
        originRef.current = { x: e.clientX, y: e.clientY };
        holdTimerRef.current = window.setTimeout(() => void start(), 150);
    };

    // Release, drag to lock, drag out to cancel — on the document, like VoiceInput: the finger leaves the button.
    useEffect(() => {
        const onUp = () => {
            if (!pressedRef.current)
                return;
            pressedRef.current = false;
            if (holdTimerRef.current)
                window.clearTimeout(holdTimerRef.current);
            if (!recordingRef.current) {
                onError(ui.recordHold);
                return;
            }
            if (!lockedRef.current)
                stop(false);
        };
        const onMove = (x: number, y: number) => {
            if (!pressedRef.current || !recordingRef.current || lockedRef.current)
                return;
            const up = Math.max(0, originRef.current.y - y);
            const left = Math.max(0, originRef.current.x - x);
            setDragUp(Math.min(up, 100));
            if (up >= 50) {
                lockedRef.current = true;
                outsideRef.current = false;
                setOutside(false);
                setState('locked');
                return;
            }
            if (isTouch) {
                setDragLeft(left);
                if (left >= 100)
                    stop(true);
                return;
            }
            const area = areaRef.current?.getBoundingClientRect();
            const out = !!area && (x < area.left || x > area.right || y < area.top - 60 || y > area.bottom);
            outsideRef.current = out;
            setOutside(out);
        };
        const onMouseMove = (e: PointerEvent) => onMove(e.clientX, e.clientY);
        const onTouchMove = (e: TouchEvent) => e.touches[0] && onMove(e.touches[0].clientX, e.touches[0].clientY);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('touchend', onUp);
        document.addEventListener('pointermove', onMouseMove);
        document.addEventListener('touchmove', onTouchMove);
        return () => {
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('touchend', onUp);
            document.removeEventListener('pointermove', onMouseMove);
            document.removeEventListener('touchmove', onTouchMove);
        };
    }, [areaRef, isTouch, onError, stop, ui]);

    // Closing the panel or leaving the page mid-recording must not leave the microphone on.
    useEffect(() => () => {
        cancelledRef.current = true;
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive')
            recorder.stop();
    }, []);

    useLayoutEffect(() => {
        if (state === 'idle')
            return;
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [state]);

    return (
        <>
            <button
                type="button"
                className="axl-sc-cb axl-sc-mic"
                aria-label={ui.record}
                title={ui.record}
                disabled={disabled}
                data-sc="mic"
                onPointerDown={onPointerDown}
                onContextMenu={e => e.preventDefault()}
            >
                <MediaIcon d={ICON.mic} />
            </button>
            {state !== 'idle' ? (
                <div className={`axl-sc-rec ${outside ? 'out' : ''}`} data-sc="recording">
                    {state === 'recording' ? (
                        <div className="axl-sc-recl" style={{ transform: `translateY(${-Math.min(dragUp, 50) / 2}px)` }}>
                            <MediaIcon d={ICON.lock} size={14} />
                            <MediaIcon d={ICON.up} size={14} />
                        </div>
                    ) : null}
                    <span className="axl-sc-recd"><i />{formatDuration(seconds)}</span>
                    {state === 'locked' ? (
                        <button type="button" className="axl-sc-recc" onClick={() => stop(true)}>{ui.recordCancel}</button>
                    ) : isTouch ? (
                        <span className="axl-sc-rech" style={{ transform: `translateX(${-Math.min(dragLeft, 100)}px)` }}>
                            <MediaIcon d={ICON.left} size={14} />{ui.recordSlideHint}
                        </span>
                    ) : (
                        <span className="axl-sc-rech">{ui.recordCancelHint}</span>
                    )}
                    {state === 'locked' ? (
                        <button type="button" className="axl-sc-recb" aria-label={ui.recordStop} onClick={() => stop(false)}>
                            <MediaIcon d={ICON.stop} size={16} fill />
                        </button>
                    ) : (
                        <span className={`axl-sc-recb ${outside ? 'out' : ''}`} aria-hidden="true"><MediaIcon d={ICON.mic} size={20} /></span>
                    )}
                </div>
            ) : null}
        </>
    );
}

/** A finished recording in the composer: listen, delete, or send. */
export function VoiceDraft({ voice, ui, onDelete }: { voice: SiteChatVoice; ui: SiteChatUi; onDelete: () => void }) {
    const url = useMemo(() => URL.createObjectURL(voice.blob), [voice.blob]);
    useEffect(() => () => URL.revokeObjectURL(url), [url]);
    return (
        <div className="axl-sc-vd" data-sc="voice-draft">
            <button type="button" className="axl-sc-cb" aria-label={ui.deleteVoice} onClick={onDelete}><MediaIcon d={ICON.trash} /></button>
            <VoicePlayer url={url} duration={voice.duration} levels={voice.levels} ui={ui} />
        </div>
    );
}
